import React from 'react';
import {
  MapPin,
  CheckCircle,
  Clock,
  Users,
  BarChart3,
  Check,
  Calendar,
} from 'lucide-react';
import './Features.css';

interface FeatureItem {
  icon: React.ReactNode;
  iconColor: 'green' | 'blue' | 'orange';
  title: string;
  description: string;
  large?: boolean;
  details?: string[];
  notifications?: { icon: React.ReactNode; iconBg: string; title: string; sub: string; time: string }[];
}

const features: FeatureItem[] = [
  {
    icon: <MapPin size={26} />,
    iconColor: 'green',
    title: 'Court Discovery',
    description: 'Search pickleball courts by name, city, or zip code with details such as court count, surface, lighting, and indoor or outdoor status.',
    large: true,
    details: [
      'Filter by indoor, outdoor, or friends at courts',
      'Sort by distance when location permission is enabled',
      'Open court details before checking in',
    ],
  },
  {
    icon: <CheckCircle size={26} />,
    iconColor: 'blue',
    title: 'Timed Check-ins',
    description: 'Check in at a court for a selected duration so friends can see where you are playing and when your check-in expires.',
  },
  {
    icon: <Clock size={26} />,
    iconColor: 'orange',
    title: 'Join Intent',
    description: 'When a friend is checked in, tap Join and share an arrival window such as 30 minutes or 1 hour.',
  },
  {
    icon: <Calendar size={26} />,
    iconColor: 'green',
    title: 'Session Scheduling',
    description: 'Create court sessions, invite friends, filter schedules by court or group, and track responses from invited players.',
  },
  {
    icon: <Users size={26} />,
    iconColor: 'blue',
    title: 'Friends, Groups & Chat',
    description: 'Find players, send friend requests, organize groups, and keep conversations going with one-to-one and group messaging.',
  },
  {
    icon: <BarChart3 size={26} />,
    iconColor: 'orange',
    title: 'Player Skill Profile',
    description: 'Set a self-rating from 1.0 to 5.5, display profile details, and show rating information when it is available on your account.',
    large: true,
    notifications: [
      {
        icon: <Check size={14} />,
        iconBg: '#0088FF',
        title: 'Self Rating',
        sub: '4.0 - Intermediate+',
        time: '',
      },
      {
        icon: <Check size={14} />,
        iconBg: '#B8FF3C',
        title: 'Profile Details',
        sub: 'Home court, city, state, and playing bio',
        time: '',
      },
    ],
  },
];

const Features: React.FC = () => {
  return (
    <section className="features" id="features">
      <div className="features__container">
        <div className="section-header">
          <div className="section-badge">Features</div>
          <h2>Practical tools to<br /><span className="text-gradient">connect and play</span></h2>
          <p className="section-subtitle">RalliPB focuses on the everyday pickleball workflow: find a court, see friend activity, coordinate a session, and keep your profile current.</p>
        </div>

        <div className="features__grid">
          {features.map((feature, i) => (
            <div className={`feature-card ${feature.large ? 'feature-card--large' : ''}`} key={i}>
              <div className={`feature-card__icon feature-card__icon--${feature.iconColor}`}>
                {feature.icon}
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>

              {feature.details && (
                <div className="feature-card__details">
                  {feature.details.map((d, j) => (
                    <div className="feature-card__detail" key={j}>
                      <Check size={16} color="#6BA314" />
                      <span>{d}</span>
                    </div>
                  ))}
                </div>
              )}

              {feature.notifications && (
                <div className="feature-card__notifications">
                  {feature.notifications.map((n, j) => (
                    <div className="notification-bubble" key={j}>
                      <div className="notification-bubble__icon" style={{ background: n.iconBg }}>
                        {n.icon}
                      </div>
                      <div className="notification-bubble__text">
                        <strong>{n.title}</strong>
                        <span>{n.sub}</span>
                      </div>
                      <span className="notification-bubble__time">{n.time}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
